// The lesson hub fields of a lesson meta (types.ts LessonHub, docs/COURSE.md "Lesson hub"), made
// safe to use. Lesson files are hand-written, so the app (app/src/lessons/index.ts) and the course
// build (scripts/src/lib/course-plan.ts) both read them through here: unknown keys and values of
// the wrong type are dropped, never thrown on.
import type { HalloweenWork, LessonDrills, LessonHub, OwnErrorSource, PronunciationFocus } from './types';
import { HALLOWEEN_WORK, OWN_ERROR_SOURCES, PRONUNCIATION_FOCI } from './types';

const strings = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string' && v !== '') : []);
const str = (x: unknown): string => (typeof x === 'string' ? x : '');

const DRILL_KEYS = ['cases', 'numbers', 'tense', 'mood', 'voice', 'degree', 'nonFinite'] as const;

function normalizeDrills(x: unknown): LessonDrills | undefined {
  if (!x || typeof x !== 'object') return undefined;
  const out: Record<string, string[]> = {};
  for (const k of DRILL_KEYS) {
    const v = strings((x as Record<string, unknown>)[k]);
    if (v.length) out[k] = v;
  }
  return Object.keys(out).length ? (out as LessonDrills) : undefined;
}

/** The Duolingo units of a meta: `duolingoUnit` may be one id or a list (a merged lesson). */
export function duolingoUnitsOf(x: unknown): string[] {
  return typeof x === 'string' ? (x ? [x] : []) : strings(x);
}

/** The hub fields of a raw lesson meta; absent fields stay absent. */
export function normalizeLessonHub(raw: unknown): LessonHub {
  if (!raw || typeof raw !== 'object') return {};
  const m = raw as Record<string, unknown>;
  const out: LessonHub = {};
  const units = duolingoUnitsOf(m['duolingoUnit']);
  if (units.length > 1) out.duolingoUnits = units;
  for (const k of ['grammar', 'grammarAlso', 'showLines', 'phrases'] as const) {
    const v = strings(m[k]);
    if (v.length) out[k] = v;
  }
  const p = m['pronunciation'];
  if (p && typeof p === 'object') {
    const pp = p as Record<string, unknown>;
    const focus = str(pp['focus']) as PronunciationFocus;
    if (PRONUNCIATION_FOCI.includes(focus)) {
      const words = strings(pp['words']);
      const lines = strings(pp['lines']);
      out.pronunciation = { focus, ...(words.length ? { words } : {}), ...(lines.length ? { lines } : {}) };
    }
  }
  const l = m['letter'];
  if (l && typeof l === 'object') {
    const fi = str((l as Record<string, unknown>)['fi']);
    const en = str((l as Record<string, unknown>)['en']);
    const model = str((l as Record<string, unknown>)['model']);
    if (fi || en) out.letter = { fi: fi || en, en: en || fi, ...(/^[a-z]+-s\d+e\d{2}-[a-z0-9]+$/.test(model) ? { model } : {}) };
  }
  const scene = str(m['scene']);
  // a show scene ("got-s3e06-1") or one of our approved everyday scenes ("ours-tervehdys", app together/everyday/)
  if (/^[a-z]+-s\d+e\d{2}-\d+$/.test(scene) || /^ours-[a-z0-9-]+$/.test(scene)) out.scene = scene;
  const drills = normalizeDrills(m['drills']);
  if (drills) out.drills = drills;
  const halloween = HALLOWEEN_WORK.filter((w): w is HalloweenWork => strings(m['halloween']).includes(w));
  if (halloween.length) out.halloween = halloween;
  // Omat virheet: the mistakes the lesson starts collecting (engine/src/pedagogy/own-errors.ts)
  const ownErrors = OWN_ERROR_SOURCES.filter((w): w is OwnErrorSource => strings(m['ownErrors']).includes(w));
  if (ownErrors.length) out.ownErrors = ownErrors;
  // Sitkeät sanat and Halloween-mittari (engine/src/pedagogy/memory-images.ts, halloween-meter.ts): flags
  if (m['memoryImages'] === true) out.memoryImages = true;
  if (m['halloweenMeter'] === true) out.halloweenMeter = true;
  // Lukupino opens here (pedagogy/reading.ts readingFromLesson)
  if (m['reading'] === true) out.reading = true;
  // per-person stress focus is used from here (pedagogy/stress-focus.ts stressFocusFromLesson)
  if (m['stressFocus'] === true) out.stressFocus = true;
  // Ääntämisvalmentaja is offered from here to a person who turned it on (pedagogy/coach.ts coachFromLesson)
  if (m['coach'] === true) out.coach = true;
  return out;
}
